import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useLocation, useParams } from "react-router-dom";
import {
  useIonRouter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import { useTranslation } from "react-i18next";

import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";

import VideoToolbar from "./components/VideoToolbar";
import SelectedFiltersBar from "./components/SelectedFiltersBar";
import VideoGrid from "./components/VideoGrid";
import VideoFiltersModal from "./components/VideoFiltersModal";

import { useVideoList } from "./hooks/useVideoList";
import { useInfiniteScrollTrigger } from "./hooks/useInfiniteScrollTrigger";
import { buildVideoNavigationPath } from "./helpers/videoNavigation";

const PAGE_SIZE = 24;
const allowedIds: string[] = [];

type RouteParams = {
  study_slug: string;
  title: string;
  type: string;
  contentType: string;
};

const DisplayVideos: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();

  const { study_slug, title, contentType } = useParams<RouteParams>();

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hoverId, setHoverId] = useState<string | null>(null);

  const [savedOnly, setSavedOnly] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("saved") === "1";
  });

  const effectiveSelectedTags = useMemo(
    () => (savedOnly ? [] : selectedTags),
    [savedOnly, selectedTags]
  );

  const allTags: Record<string, string[]> = {
    [t("mentalplus_filtre_ile")]: [
      t("mentalplus_tab_ile_amour"),
      t("mentalplus_tag_college"),
      t("mentalplus_tab_ile_amis"),
      t("mentalplus_tab_ile_moi"),
      t("mentalplus_tab_ile_ecrans"),
      t("mentalplus_tab_ile_famille"),
    ],
    [t("mentalplus_filtre_categories")]: [
      t("mentalplus_filtre_categories_explications"),
      t("mentalplus_filtre_categories_solutions"),
    ],
    [t("mentalplus_filtre_realisee")]: [
      t("mentalplus_filtre_realisee_professionnels"),
      t("mentalplus_filtre_realisee_communaute"),
    ],
    [t("mentalplus_filtre_realisee_2")]: [
      t("mentalplus_filtre_realisee_collegiens"),
      t("mentalplus_filtre_realisee_lyceens"),
      t("mentalplus_filtre_realisee_etudiants"),
      t("mentalplus_filtre_realisee_jeunes_actifs"),
      t("mentalplus_filtre_realisee_sans_activite"),
    ],
    [t("mentalplus_filtre_trier")]: [
      t("mentalplus_filtre_trier_popularite"),
      t("mentalplus_filtre_trier_recent"),
      t("mentalplus_filtre_trier_ancien"),
    ],
  };

  const baseTags = useMemo(
    () => [title, contentType].filter(Boolean),
    [title, contentType]
  );

  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      headerBackground: "white",
      title: savedOnly
        ? "mentalplus_videos_enregistrees"
        : "mentalplus_tab_videos",
      welcomePage: false,
    });
  });

  useIonViewWillLeave(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      welcomePage: false,
      headerBackground: "transparent",
    });
  });

  useEffect(() => {
    const syncSavedOnlyFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      setSavedOnly(params.get("saved") === "1");
    };

    syncSavedOnlyFromUrl();

    window.addEventListener("popstate", syncSavedOnlyFromUrl);
    return () => window.removeEventListener("popstate", syncSavedOnlyFromUrl);
  }, [location.pathname]);

  useEffect(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      headerBackground: "white",
      title: savedOnly
        ? "mentalplus_videos_enregistrees"
        : "mentalplus_tab_videos",
      welcomePage: false,
    });
  }, [savedOnly, setResonanceLayout]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [selectedTags, query, savedOnly]);

  useEffect(() => {
    if (savedOnly && selectedTags.length > 0) {
      setSelectedTags([]);
    }
    setPanelOpen(false);
  }, [savedOnly, selectedTags.length]);

  const { videos, loading, loadingMore, hasMore, loadMore } = useVideoList({
    baseTags,
    selectedTags: effectiveSelectedTags,
    query,
    allowedIds,
    pageSize: PAGE_SIZE,
    savedOnly,
  });

  const { lastItemRef } = useInfiniteScrollTrigger({
    hasMore,
    loading: loading || loadingMore,
    onLoadMore: loadMore,
  });

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag)
        ? prev.filter((item) => item !== tag)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSelectedTags([]);
    setPanelOpen(false);
  };

  const handleVideoClick = (videoUid: string) => {
    const path = buildVideoNavigationPath({
      studySlug: study_slug,
      title,
      contentType,
      videoUid,
      selectedTags: effectiveSelectedTags,
      search: query,
    });

    ionRouter.push(path, "forward", "push");
  };

  const handleOpenSaved = () => {
    const nextSavedOnly = !savedOnly;
    const nextUrl = nextSavedOnly
      ? `/${study_slug}/tabs/videos?saved=1`
      : `/${study_slug}/tabs/videos`;

    setSavedOnly(nextSavedOnly);
    ionRouter.push(nextUrl, nextSavedOnly ? "forward" : "back", "push");
  };

  const handleBackFromSaved = () => {
    setSavedOnly(false);
    ionRouter.push(`/${study_slug}/tabs/videos`, "back", "pop");
  };

  return (
    <ResonanceLayout isConnected>
      <PageWrapper>
        <TopArea>
          {savedOnly ? (
            <BackBar>
              <BackButton onClick={handleBackFromSaved}>
                ← {t("mentalplus_video_retour")}
              </BackButton>
            </BackBar>
          ) : (
            <>
              <VideoToolbar
                query={query}
                onQueryChange={setQuery}
                onOpenFilters={() => setPanelOpen(true)}
                onOpenSaved={handleOpenSaved}
              />

              <SelectedFiltersBar
                selectedTags={selectedTags}
                onToggleTag={toggleTag}
                onClear={clearFilters}
              />
            </>
          )}
        </TopArea>

        <ContentArea $hasChips={!savedOnly && selectedTags.length > 0}>
          <VideoGrid
            videos={videos}
            loading={loading}
            loadingMore={loadingMore}
            hoverId={hoverId}
            onHoverChange={setHoverId}
            lastVideoRef={lastItemRef}
            onVideoClick={handleVideoClick}
            savedOnly={savedOnly}
          />
        </ContentArea>

        {!savedOnly && (
          <VideoFiltersModal
            isOpen={panelOpen}
            title={t("mentalplus_video_filtres")}
            sections={allTags}
            selectedTags={selectedTags}
            onClose={() => setPanelOpen(false)}
            onToggleTag={toggleTag}
          />
        )}
      </PageWrapper>
    </ResonanceLayout>
  );
};

export default DisplayVideos;

/* -------------------------------------------------------------------------- */
/*                                   STYLES                                   */
/* -------------------------------------------------------------------------- */

const PageWrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: white;
  position: relative;
  overflow-x: hidden;
`;

const TopArea = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  width: 100%;
  background: white;
  z-index: 10000;
`;

const BackBar = styled.div`
  background: white;
  padding: 8px;
`;

const BackButton = styled.button`
  flex: 0 0 auto;
  min-width: 84px;
  height: 58px;
  border: none;
  border-radius: 14px;
  background: #ffce48;
  color: #1b1b1b;
  cursor: pointer;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-size: 12px;
  line-height: 12px;
  font-weight: 600;
  white-space: nowrap;
  padding: 0 12px;

  @media (max-width: 420px) {
    min-width: 70px;
    height: 54px;
    font-size: 11px;
  }
`;

const ContentArea = styled.div<{ $hasChips: boolean }>`
  padding-top: ${({ $hasChips }) => ($hasChips ? "120px" : "74px")};
`;